import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { useSnackbar } from "src/components/snackbar";
import { propuestasApi } from "src/services/propuestasApi";
import { fechaExpiracion } from "src/components/propuesta/utils";
import { fechaActual } from "src/utils/formatTime";

export default function ModalExtenderPropuesta({
  open,
  setOpen,
  propuesta,
  tipo, // "extender" | "reactivar"
}) {
  const [dias, setDias] = useState(tipo === "extender" ? 7 : 5);
  const { enqueueSnackbar } = useSnackbar();

  const [extenderPropuesta, { isLoading: extendiendo }] =
    propuestasApi.useExtenderPropuestaMutation();
  const [reactivarPropuesta, { isLoading: reactivando }] =
    propuestasApi.useReactivarPropuestaMutation();

  const isLoading = extendiendo || reactivando;

  const handleClose = useCallback(() => {
    setOpen(false);
    setDias(tipo === "extender" ? 7 : 5);
  }, [setOpen, tipo]);

  const handleSubmit = useCallback(async () => {
    try {
      const payload =
        tipo === "extender"
          ? { diasExtension: dias }
          : { diasReactivacion: dias };

      if (tipo === "extender") {
        await extenderPropuesta({ id: propuesta.id, ...payload }).unwrap();
        enqueueSnackbar(
          `Propuesta extendida por ${dias} días correctamente`,
          {},
        );
      } else {
        const resultado = await reactivarPropuesta({
          id: propuesta.id,
          ...payload,
        }).unwrap();

        // El aviso lo redacta el backend porque el resultado depende de por qué
        // había caducado la propuesta: cuando caducó esperando una respuesta
        // nuestra, el enlace del cliente se conserva y los días elegidos no se
        // aplican, así que anunciarlos acá le prometería al vendedor un plazo
        // que nadie movió.
        enqueueSnackbar(
          resultado?.mensaje || `Propuesta reactivada por ${dias} días`,
          {},
        );
      }

      handleClose();
    } catch (error) {
      enqueueSnackbar(
        error?.data?.message || "Error al procesar la solicitud",
        { variant: "error" },
      );
    }
  }, [
    tipo,
    dias,
    propuesta.id,
    extenderPropuesta,
    reactivarPropuesta,
    enqueueSnackbar,
    handleClose,
  ]);

  const calcularNuevaFecha = () => {
    if (tipo === "extender") {
      // Para extensión: desde la fecha actual de expiración + días
      return fechaExpiracion(propuesta, "moment")
        .add(dias, "days")
        .format("DD/MM/YYYY");
    } else {
      // Para reactivación: desde hoy + días
      return fechaActual().add(dias, "days").format("DD/MM/YYYY");
    }
  };

  const maxDias = tipo === "extender" ? 30 : 15;
  const opciones = Array.from({ length: maxDias }, (_, i) => i + 1);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {tipo === "extender"
          ? "Extender Plazo de Propuesta"
          : "Reactivar Propuesta"}
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Propuesta:{" "}
            {propuesta?.nombreSolicitante || propuesta?.nombreCliente}
          </Typography>
        </Box>

        {tipo === "extender" && (
          <Alert severity="info" style={{ marginBottom: 16 }}>
            <strong>Fecha actual de expiración:</strong>{" "}
            {fechaExpiracion(propuesta)}
          </Alert>
        )}

        {tipo === "reactivar" && (
          <Alert severity="warning" style={{ marginBottom: 16 }}>
            Esta propuesta expiró el: {fechaExpiracion(propuesta)}
          </Alert>
        )}

        <FormControl fullWidth margin="normal">
          <InputLabel>
            {tipo === "extender" ? "Días a extender" : "Días de reactivación"}
          </InputLabel>
          <Select value={dias} onChange={(e) => setDias(e.target.value)}>
            {opciones.map((dia) => (
              <MenuItem key={dia} value={dia}>
                {dia} día{dia > 1 ? "s" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Alert severity="success" style={{ marginTop: 16 }}>
          <strong>Nueva fecha de expiración:</strong> {calcularNuevaFecha()}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={isLoading}
          variant="contained"
          color="primary"
        >
          {tipo === "extender" ? "Extender" : "Reactivar"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
